import { useTranslation } from "react-i18next";
import { sentence } from "@/utils/coach";
import { Badge } from "@/atoms/badge";
import { Button } from "@/atoms/button";
import { FactList, FactRow } from "@/components/fact-row";
import { formatTableMoney, fromCents } from "@/utils/money";
import type { PublicInvoiceDetails } from "@/data/invoice";

/**
 * An invoice that is settled (spec receipt 1.1; checkout 1.4 region `done`).
 *
 * The same receipt shape as the checkout's, in one sentence and three facts: a badge, a
 * headline, what was paid and for which invoice, and the one tertiary control that opens the
 * document. No orange, no tick disc, no celebration.
 *
 * `processing` is the neutral badge and the softer headline: the money has left but the bank has
 * not confirmed it, and telling someone it is paid before it is would be reporting an action
 * that has not happened.
 */

export interface InvoiceReceiptProps {
  invoice: PublicInvoiceDetails;
  coachName: string;
  status: "succeeded" | "processing";
}

export function InvoiceReceipt({
  invoice,
  coachName,
  status,
}: InvoiceReceiptProps) {
  const { t } = useTranslation();
  const processing = status === "processing";
  const doc = invoice.invoice;
  const number = doc.number ?? doc.id.slice(0, 8).toUpperCase();

  return (
    <section className="gap-gk-6 flex flex-col">
      <div className="gap-gk-4 flex flex-col items-start">
        <Badge tone={processing ? "neutral" : "success"}>
          {processing
            ? t("gymkeePay.common.statusProcessing")
            : t("gymkeePay.common.statusPaid")}
        </Badge>
        <h1 className="text-[32px] leading-[1.15] font-semibold text-[var(--gk-text-primary)]">
          {processing
            ? t("gymkeePay.receipt.headlineProcessing")
            : t("gymkeePay.invoice.alreadyPaidHeadline")}
        </h1>
        <p className="text-[15px] leading-[1.55] text-[var(--gk-text-secondary)]">
          {processing
            ? sentence(
                t("gymkeePay.receipt.lineProcessing", { coach: coachName }),
              )
            : sentence(
                t("gymkeePay.invoice.alreadyPaidLine", {
                  coach: coachName,
                  number,
                }),
              )}
        </p>
      </div>

      <FactList>
        <FactRow
          label={t("gymkeePay.invoice.amountPaid")}
          value={formatTableMoney(fromCents(doc.total), doc.currency)}
          numeric
        />
        <FactRow label={t("gymkeePay.invoice.fallbackTitle")} value={number} />
      </FactList>

      {doc.invoicePdf && (
        <Button
          variant="tertiary"
          href={doc.invoicePdf}
          target="_blank"
          className="ds-pay-hand self-start"
        >
          {t("gymkeePay.invoice.downloadInvoice")}
        </Button>
      )}

      <p className="text-[13px] leading-[1.5] text-[var(--gk-text-tertiary)]">
        {t("gymkeePay.receipt.footerOneOff", { coach: coachName })}
      </p>
    </section>
  );
}
